import { FileDown, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useGeneratePdf } from '@/features/pdf-generation/hooks/use-generate-pdf'
import { usePdfTemplates } from '@/features/pdf-generation/hooks/use-pdf-templates'
import { resolveLanguage } from '@/services/i18n/languages'

interface GeneratePdfMenuProps {
  /** The kind of subject the templates should target, e.g. `"identity"`. */
  subjectType: string
  /** Backend identifier for the subject (Neo4j elementId, etc.). */
  subjectRef: string
  /** Case the subject lives in. */
  caseId: string
  /** Visual size of the icon trigger. Defaults to `"icon-sm"`. */
  size?: 'icon-xs' | 'icon-sm' | 'icon' | 'icon-lg'
  /** Trigger variant. Defaults to `"ghost"`. */
  variant?: 'outline' | 'default' | 'ghost'
  /** Optional override for the accessible label (defaults to `pdf.actions.generate`). */
  label?: string
  /** Where the menu should align relative to its trigger. */
  align?: 'start' | 'center' | 'end'
}

/**
 * Reusable per-subject "Generate document" menu. Lists every PDF template
 * whose `subject_type` matches `subjectType`, posts to /v1/pdf/generate when
 * one is picked and downloads the resulting blob.
 *
 * Renders as an icon-only button to keep the trigger unobtrusive inside
 * tables and headers; the action name is exposed via `aria-label` and the
 * native tooltip.
 */
export function GeneratePdfMenu({
  subjectType,
  subjectRef,
  caseId,
  size = 'icon-sm',
  variant = 'ghost',
  label,
  align = 'end',
}: GeneratePdfMenuProps) {
  const { t, i18n } = useTranslation()
  const { data: templates, isLoading: isLoadingTemplates } = usePdfTemplates(subjectType)
  const generate = useGeneratePdf()

  const triggerLabel = label ?? t('pdf.actions.generate')
  const hasTemplates = (templates?.length ?? 0) > 0
  const isBusy = generate.isPending

  const onSelect = (templateName: string, locales: string[]) => {
    // Prefer the user's current UI locale; fall back to `en`, or whatever the
    // template ships when neither is available.
    const uiLocale = resolveLanguage(i18n.language)
    const locale = locales.includes(uiLocale)
      ? uiLocale
      : locales.includes('en')
        ? 'en'
        : locales[0]

    generate.mutate(
      { template: templateName, subjectRef, caseId, locale },
      {
        onSuccess: () => {
          toast.success(t('pdf.actions.success'))
        },
        onError: () => {
          toast.error(t('pdf.actions.error'))
        },
      }
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={variant}
          size={size}
          disabled={isBusy || isLoadingTemplates || !hasTemplates}
          aria-label={triggerLabel}
          title={triggerLabel}
          onClick={e => e.stopPropagation()}
        >
          {isBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align={align}
        // Phone: wide enough for a template title to stay on one line (the menu
        // otherwise takes the icon trigger's width).
        className="max-md:min-w-56"
        // Stop row-level click handlers from firing when interacting with the menu.
        onClick={e => e.stopPropagation()}
      >
        {isLoadingTemplates && (
          <DropdownMenuItem disabled>{t('pdf.actions.loading')}</DropdownMenuItem>
        )}
        {!isLoadingTemplates && !hasTemplates && (
          <DropdownMenuItem disabled>{t('pdf.actions.empty')}</DropdownMenuItem>
        )}
        {templates?.map(template => (
          <DropdownMenuItem
            key={template.name}
            disabled={isBusy}
            onSelect={() => onSelect(template.name, template.locales)}
          >
            {t(template.title_key, { defaultValue: template.name })}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
